import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import ksuLogo from '../../assets/images/ksu-logo.png';
import { PROGRAM_EXCEL_FILTERS } from './data';
import OverseasBachelorContent from './OverseasBachelorContent';
import ProgramFilters from './ProgramFilters';

const CARD_THEMES = [
  {
    border: 'border-sky-200/80',
    hover: 'hover:border-sky-400/60 hover:shadow-sky-200/50',
    bar: 'from-sky-500 to-cyan-400',
    surface: 'from-sky-50/90 via-white to-white',
    label: 'text-sky-700',
    badge: 'bg-sky-100 text-sky-800 ring-sky-200/60',
    chip: 'bg-sky-50/80 text-sky-900 ring-sky-100',
    divider: 'border-sky-100',
    reqLabel: 'text-sky-600/90',
  },
  {
    border: 'border-teal-200/80',
    hover: 'hover:border-teal-400/60 hover:shadow-teal-200/50',
    bar: 'from-teal-600 to-emerald-400',
    surface: 'from-teal-50/90 via-white to-white',
    label: 'text-teal-700',
    badge: 'bg-teal-100 text-teal-800 ring-teal-200/60',
    chip: 'bg-teal-50/80 text-teal-900 ring-teal-100',
    divider: 'border-teal-100',
    reqLabel: 'text-teal-600/90',
  },
  {
    border: 'border-indigo-200/80',
    hover: 'hover:border-indigo-400/60 hover:shadow-indigo-200/50',
    bar: 'from-indigo-600 to-violet-400',
    surface: 'from-indigo-50/90 via-white to-white',
    label: 'text-indigo-700',
    badge: 'bg-indigo-100 text-indigo-800 ring-indigo-200/60',
    chip: 'bg-indigo-50/80 text-indigo-900 ring-indigo-100',
    divider: 'border-indigo-100',
    reqLabel: 'text-indigo-600/90',
  },
  {
    border: 'border-amber-200/80',
    hover: 'hover:border-amber-400/60 hover:shadow-amber-200/50',
    bar: 'from-amber-600 to-orange-400',
    surface: 'from-amber-50/90 via-white to-white',
    label: 'text-amber-800',
    badge: 'bg-amber-100 text-amber-900 ring-amber-200/60',
    chip: 'bg-amber-50/80 text-amber-900 ring-amber-100',
    divider: 'border-amber-100',
    reqLabel: 'text-amber-700/90',
  },
  {
    border: 'border-violet-200/80',
    hover: 'hover:border-violet-400/60 hover:shadow-violet-200/50',
    bar: 'from-violet-600 to-fuchsia-400',
    surface: 'from-violet-50/90 via-white to-white',
    label: 'text-violet-700',
    badge: 'bg-violet-100 text-violet-800 ring-violet-200/60',
    chip: 'bg-violet-50/80 text-violet-900 ring-violet-100',
    divider: 'border-violet-100',
    reqLabel: 'text-violet-600/90',
  },
  {
    border: 'border-rose-200/80',
    hover: 'hover:border-rose-400/60 hover:shadow-rose-200/50',
    bar: 'from-rose-600 to-pink-400',
    surface: 'from-rose-50/90 via-white to-white',
    label: 'text-rose-700',
    badge: 'bg-rose-100 text-rose-800 ring-rose-200/60',
    chip: 'bg-rose-50/80 text-rose-900 ring-rose-100',
    divider: 'border-rose-100',
    reqLabel: 'text-rose-600/90',
  },
];

const PROGRAM_LEVELS = [
  { id: 'postgraduate', label: 'Postgraduate Studies' },
  { id: 'bachelor', label: 'Bachelor’s Degree' },
];

const GENDER_FILTER_OPTIONS = [
  { id: 'both', label: 'Male & Female' },
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
];

function matchesGenderFilter(targetGroup, genderFilterId) {
  if (genderFilterId === 'both') return true;
  if (genderFilterId === 'male') {
    return targetGroup === 'Male' || targetGroup === 'Male- Female';
  }
  if (genderFilterId === 'female') {
    return targetGroup === 'Female' || targetGroup === 'Male- Female';
  }
  return true;
}

const LANGUAGE_FILTER_OPTIONS = [
  { id: 'all', label: 'All languages' },
  { id: 'arabic', label: 'Arabic' },
  { id: 'english', label: 'English' },
  { id: 'french', label: 'French' },
];

function matchesLanguageFilter(language, languageFilterId) {
  if (languageFilterId === 'all') return true;
  if (languageFilterId === 'arabic') {
    return language === 'Arabic' || language === 'Arabic- English';
  }
  if (languageFilterId === 'english') {
    return language === 'English' || language === 'Arabic- English';
  }
  if (languageFilterId === 'french') {
    return language === 'French';
  }
  return true;
}

function matchesProgramSearch(row, query) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  const haystack = [row.program, row.college, row.department, row.discipline]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(normalized);
}

const ALL_PROGRAM_TYPES_ID = 'all';
const ALL_COLLEGES_ID = 'all';

const PROGRAM_TYPE_FILTER_OPTIONS = [
  { id: ALL_PROGRAM_TYPES_ID, label: 'All program types' },
  { id: 'phd', label: 'PhD' },
  { id: 'master-msc', label: 'Master and MSc' },
  { id: 'higher-diploma', label: 'Higher Diploma' },
  { id: 'dsc', label: 'DSc' },
];

function getProgramType(programName) {
  const name = programName.trim();
  if (name.startsWith('Higher Diploma')) return 'higher-diploma';
  if (name.startsWith('DSc')) return 'dsc';
  if (name.startsWith('PhD') || name.startsWith('A Doctorate')) return 'phd';
  if (
    name.startsWith('Master') ||
    name.startsWith('MSc') ||
    name.startsWith('MSC') ||
    name.startsWith('Executive Master') ||
    name.startsWith('Professional Master')
  ) {
    return 'master-msc';
  }
  return null;
}

function buildSelectOptions(values, allLabel) {
  const unique = [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return [
    { id: 'all', label: allLabel },
    ...unique.map((value) => ({ id: value, label: value })),
  ];
}

function matchesProgramTypeFilter(program, programFilterId) {
  if (programFilterId === ALL_PROGRAM_TYPES_ID) return true;
  return getProgramType(program) === programFilterId;
}

function matchesCollegeFilter(college, collegeFilterId) {
  if (collegeFilterId === ALL_COLLEGES_ID) return true;
  return college === collegeFilterId;
}

const CARDS_PER_PAGE = 9;
const VISIBLE_PAGE_COUNT = 7;

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= VISIBLE_PAGE_COUNT) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  let start = currentPage - Math.floor(VISIBLE_PAGE_COUNT / 2);
  let end = start + VISIBLE_PAGE_COUNT - 1;

  if (start < 1) {
    start = 1;
    end = VISIBLE_PAGE_COUNT;
  } else if (end > totalPages) {
    end = totalPages;
    start = totalPages - VISIBLE_PAGE_COUNT + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function CardPagination({ currentPage, totalPages, totalItems, onPageChange, onScrollToTop }) {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * CARDS_PER_PAGE + 1;
  const end = Math.min(currentPage * CARDS_PER_PAGE, totalItems);

  const goToPage = (page) => {
    onPageChange(page);
    onScrollToTop?.();
  };

  return (
    <nav
      className="mt-8 flex flex-col items-center gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:justify-between"
      aria-label="Program cards pagination"
    >
      <p className="text-sm text-slate-600">
        Showing <span className="font-semibold text-slate-800">{start}</span>–
        <span className="font-semibold text-slate-800">{end}</span> of{' '}
        <span className="font-semibold text-slate-800">{totalItems}</span> programs
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={currentPage === 1}
          onClick={() => goToPage(1)}
        >
          First
        </button>
        <button
          type="button"
          className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          Previous
        </button>
        {getVisiblePageNumbers(currentPage, totalPages).map((page) => (
          <button
            key={page}
            type="button"
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
            className={`min-w-[2.5rem] rounded-full px-3 py-2 text-sm font-semibold transition ${
              page === currentPage
                ? 'bg-[#0084BD] text-white shadow-md'
                : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
            onClick={() => goToPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
        >
          Next
        </button>
        <button
          type="button"
          className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(totalPages)}
        >
          Last
        </button>
      </div>
    </nav>
  );
}

function ProgramCard({ row, themeIndex = 0 }) {
  const theme = CARD_THEMES[themeIndex % CARD_THEMES.length];
  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-gradient-to-br p-6 shadow-md ring-1 ring-black/[0.03] transition duration-300 hover:-translate-y-0.5 hover:shadow-xl ${theme.border} ${theme.hover} ${theme.surface}`}
    >
      <div
        className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${theme.bar}`}
        aria-hidden
      />
      <div className={`mb-4 flex flex-wrap items-start justify-between gap-2 border-b pb-4 pt-1 ${theme.divider}`}>
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${theme.label}`}>
            {row.college}
          </p>
          <p className="mt-1 text-sm text-slate-600">{row.department}</p>
        </div>
        <p className={`rounded-full px-3 py-1 text-sm font-semibold ring-1 ${theme.badge}`}>
          {row.tuition}
        </p>
      </div>
      <h2 className="text-lg font-bold text-slate-900">{row.program}</h2>
      <p className="mt-2 text-sm font-medium text-slate-700">
        Discipline / track: <span className="font-normal">{row.discipline}</span>
      </p>
      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <span className={`rounded-lg px-3 py-1 ring-1 ${theme.chip}`}>
          Language: <strong>{row.language}</strong>
        </span>
        <span className={`rounded-lg px-3 py-1 ring-1 ${theme.chip}`}>
          Target: <strong>{row.targetGroup}</strong>
        </span>
      </div>
      <div className="mt-4 flex-1">
        <p className={`text-xs font-semibold uppercase ${theme.reqLabel}`}>
          Program’s requirements
        </p>
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
          {row.requirements}
        </p>
      </div>
    </article>
  );
}

export default function ProgramsPage() {
  const cardsSectionRef = useRef(null);
  const [activeLevelId, setActiveLevelId] = useState(PROGRAM_LEVELS[0].id);
  const [activeFilterId, setActiveFilterId] = useState(PROGRAM_EXCEL_FILTERS[0].id);
  const [genderFilterId, setGenderFilterId] = useState(GENDER_FILTER_OPTIONS[0].id);
  const [languageFilterId, setLanguageFilterId] = useState(LANGUAGE_FILTER_OPTIONS[0].id);
  const [programFilterId, setProgramFilterId] = useState(PROGRAM_TYPE_FILTER_OPTIONS[0].id);
  const [collegeFilterId, setCollegeFilterId] = useState(ALL_COLLEGES_ID);
  const [programSearch, setProgramSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const scrollCardsSectionToTop = () => {
    cardsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const active = PROGRAM_EXCEL_FILTERS.find((f) => f.id === activeFilterId) ?? PROGRAM_EXCEL_FILTERS[0];
  const isPostgraduate = activeLevelId === 'postgraduate';
  const collegeOptions = useMemo(
    () => buildSelectOptions(active.rows.map((row) => row.college), 'All colleges'),
    [active.rows],
  );
  const filteredRows = active.rows.filter(
    (row) =>
      matchesGenderFilter(row.targetGroup, genderFilterId) &&
      matchesLanguageFilter(row.language, languageFilterId) &&
      matchesProgramTypeFilter(row.program, programFilterId) &&
      matchesCollegeFilter(row.college, collegeFilterId) &&
      matchesProgramSearch(row, programSearch),
  );
  const hasActiveFilters =
    genderFilterId !== GENDER_FILTER_OPTIONS[0].id ||
    languageFilterId !== LANGUAGE_FILTER_OPTIONS[0].id ||
    programFilterId !== PROGRAM_TYPE_FILTER_OPTIONS[0].id ||
    collegeFilterId !== ALL_COLLEGES_ID ||
    programSearch.trim().length > 0;

  const clearFilters = () => {
    setGenderFilterId(GENDER_FILTER_OPTIONS[0].id);
    setLanguageFilterId(LANGUAGE_FILTER_OPTIONS[0].id);
    setProgramFilterId(PROGRAM_TYPE_FILTER_OPTIONS[0].id);
    setCollegeFilterId(ALL_COLLEGES_ID);
    setProgramSearch('');
  };
  const totalCards = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCards / CARDS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * CARDS_PER_PAGE;
  const paginatedRows = filteredRows.slice(pageStart, pageStart + CARDS_PER_PAGE);

  useEffect(() => {
    setProgramFilterId(PROGRAM_TYPE_FILTER_OPTIONS[0].id);
    setCollegeFilterId(ALL_COLLEGES_ID);
  }, [activeFilterId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeFilterId,
    genderFilterId,
    languageFilterId,
    programFilterId,
    collegeFilterId,
    programSearch,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTopButton(window.scrollY > 300);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 md:px-0 py-3">
          <a className="flex shrink-0 items-center" href="/">
            <img
              src={ksuLogo}
              alt="King Saud University"
              className="h-10 w-auto sm:h-12"
            />
          </a>
          <div className="flex items-center gap-4 text-sm font-semibold">
            <span className="hidden text-slate-600 sm:inline">Programs</span>
            <a
              className="rounded-full px-4 py-2 text-[#0084BD] ring-1 ring-[#0084BD]/30 transition hover:bg-[#0084BD]/10"
              href="/"
            >
              Home
            </a>
          </div>
        </div>
      </nav>

      <main className="min-h-screen bg-slate-50 px-6 py-12 text-slate-900">
        <div className="mx-auto max-w-6xl">
          <header>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#0084BD]">
              Programs
            </p>
            <h1 className="mt-3 text-3xl font-bold sm:text-4xl">KSU programs</h1>
            <p className="mt-3 max-w-3xl text-lg text-slate-600">
              Choose a program level, then browse the available program data.
            </p>
          </header>

          <div
            className="mt-8 flex flex-wrap gap-3"
            role="tablist"
            aria-label="Program level"
          >
            {PROGRAM_LEVELS.map((level) => (
              <button
                key={level.id}
                type="button"
                role="tab"
                aria-selected={activeLevelId === level.id}
                className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                  activeLevelId === level.id
                    ? 'bg-[#0084BD] text-white shadow-md'
                    : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
                }`}
                onClick={() => setActiveLevelId(level.id)}
              >
                {level.label}
              </button>
            ))}
          </div>

          {isPostgraduate ? (
            <>
              <div
                className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-4"
                role="tablist"
                aria-label="Program data by Excel file"
              >
                {PROGRAM_EXCEL_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={activeFilterId === f.id}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      activeFilterId === f.id
                        ? 'bg-[#0084BD] text-white shadow-md'
                        : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
                    }`}
                    onClick={() => setActiveFilterId(f.id)}
                  >
                    {f.filterLabel}
                  </button>
                ))}
              </div>

              <section
                ref={cardsSectionRef}
                className="scroll-mt-24 mt-8 rounded-3xl bg-white p-6 shadow-xl sm:p-10"
                aria-live="polite"
              >
                <h2 className="text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
                  {active.sectionTitle}
                </h2>

                <div className="mt-6">
                  <ProgramFilters
                    programSearch={programSearch}
                    onProgramSearchChange={setProgramSearch}
                    genderFilterId={genderFilterId}
                    onGenderFilterChange={setGenderFilterId}
                    genderOptions={GENDER_FILTER_OPTIONS}
                    languageFilterId={languageFilterId}
                    onLanguageFilterChange={setLanguageFilterId}
                    languageOptions={LANGUAGE_FILTER_OPTIONS}
                    programFilterId={programFilterId}
                    onProgramFilterChange={setProgramFilterId}
                    programOptions={PROGRAM_TYPE_FILTER_OPTIONS}
                    collegeFilterId={collegeFilterId}
                    onCollegeFilterChange={setCollegeFilterId}
                    collegeOptions={collegeOptions}
                    resultCount={filteredRows.length}
                    totalCount={active.rows.length}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </div>

                {filteredRows.length === 0 ? (
                  <p className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-slate-600">
                    {active.rows.length === 0
                      ? 'No rows yet for this file. Send the file title and table, and we will add cards here.'
                      : 'No programs match the selected filters. Try choosing different options.'}
                  </p>
                ) : (
                  <>
                    <ul className="mt-8 grid list-none gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
                      {paginatedRows.map((row, index) => (
                        <li key={`${row.program}-${row.discipline}-${pageStart + index}`}>
                          <ProgramCard row={row} themeIndex={pageStart + index} />
                        </li>
                      ))}
                    </ul>
                    <CardPagination
                      currentPage={safePage}
                      totalPages={totalPages}
                      totalItems={totalCards}
                      onPageChange={setCurrentPage}
                      onScrollToTop={scrollCardsSectionToTop}
                    />
                  </>
                )}
              </section>
            </>
          ) : (
            <div aria-live="polite">
              <OverseasBachelorContent />
            </div>
          )}
        </div>

        <footer className="mt-6 border-t border-slate-200 pt-3 text-xs text-slate-500">
          <p className="text-center">Copyright © 2026 King Saud University</p>
        </footer>
      </main>
      {showScrollTopButton && (
        <button
          type="button"
          aria-label="Scroll to top"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#0084BD] text-white shadow-lg transition hover:bg-[#0073A5] focus:outline-none focus:ring-2 focus:ring-[#0084BD]/50 focus:ring-offset-2"
        >
          <ArrowUp className="h-5 w-5" aria-hidden />
        </button>
      )}
    </>
  );
}